import { useApolloClient } from '@apollo/client'
import {
  useErrorHandlingMutation,
  useErrorHandlingQuery,
} from '@backoffice-frontend/data-fetching'
import { FormComboboxField, useForm } from '@backoffice-frontend/forms'
import { DispatchingStatus, TripType } from '@backoffice-frontend/graphql'
import {
  getTypeFromTaxonomy,
  ODD_TYPE_TO_ELEMENT_TYPE,
  useOverwatchMapListTaxonomiesLazyQuery,
} from '@backoffice-frontend/overwatch-map-common'
import { useMoiaSuccessNotification } from '@backoffice-frontend/patterns'
import { useBoumClaimsConsumer } from '@backoffice-frontend/restricted'
import { usePaceValidators } from '@backoffice-frontend/validators'
import {
  CheckmarkIcon,
  PlusIcon,
  SearchIcon,
  XIcon,
} from '@moia-dev/pace-icons'
import {
  ActionItem,
  ActionMenu,
  Button,
  Heading,
  IconButton,
  Input,
  LoadingIndicator,
  Text,
  useActionMenu,
  useDialog,
} from '@moia-dev/pace-web'
import { useQueryState } from 'nuqs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router'
import { RouteBuilderAreaRoutes } from '../../RouteBuilderAreaRoutes'
import { useSharedMapContext } from '../SharedMapContext'
import {
  RouteBuilderSavedRouteDetailsDocument,
  type RouteBuilderSavedRouteDetailsQuery,
  useRouteBuilderOddElementsForRouteLazyQuery,
  useRouteBuilderOverwatchFleetsQuery,
  useRouteBuilderSavedRouteDetailsQuery,
  useRouteBuilderValidateRouteInternalMutation,
} from '../TripEditor/TripEditor.hook'
import type { EditingTrip } from '../TripEditor/TripEditorView'
import type {
  AssigningVehicle,
  ODDMapElement,
  Waypoint,
} from '../TripEditor/types'
import { computeMapPadding, detailsToSegmentInputs } from '../utils'
import { RenameDialog } from './RenameDialog'
import {
  type RouteLegInfo,
  SavedRouteDetailsPanel,
} from './SavedRouteDetailsPanel'
import { type SavedTrip, SavedTripCard } from './SavedTripCard'
import {
  type RouteBuilderDispatchingStatusQuery,
  useRouteBuilderAssignSavedTripMutation,
  useRouteBuilderBackofficeUserByIdLazyQuery,
  useRouteBuilderDispatchingStatusLazyQuery,
  useRouteBuilderOverwatchVehiclesQuery,
  useRouteBuilderSavedRoutesQuery,
  useRouteBuilderSavedTripsByVtrsQuery,
  useRouteBuilderSetSavedTripArchivedMutation,
} from './SavedTrips.hook'
import { useOptionalSavedTripsTabs } from './SavedTripsTabsContext'
import { VtrAssociationDialog } from './VtrAssociationDialog'

type SavedTripsViewProps = {
  metropolisId: number
  mapCenter: [number, number]
  /** When provided, "Assign" on a saved trip calls the mutation directly with this vehicle's VIN — no dialog. */
  assigningVehicle?: AssigningVehicle
  /** Modal mode: called instead of navigate() when opening the create/duplicate editor. */
  onNavigateToCreate?: (opts?: {
    duplicateFrom?: EditingTrip
    duplicateFromTripId?: string
  }) => void
  /** Modal mode: called instead of navigate() when opening the edit editor. */
  onNavigateToEdit?: (tripId: string) => void
  /** Modal mode: replaces the location-based inEditor detection. */
  inEditorOverride?: boolean
  /** Called after a saved-trip dispatch completes successfully — used by modal host to close the modal. */
  onAssignSuccess?: () => void
}

/** A vehicle the user can pick by its number; `vin` is what the mutation needs. */
type VehicleOption = {
  vin: string
  number: string
}

type AssignVehicleFormValues = {
  vehicle: VehicleOption | null
}

/** DOM id of the portal host for the assign dialog's vehicle-number dropdown. */
const ASSIGN_VEHICLE_DROPDOWN_PORTAL_ID =
  'route-builder-assign-vehicle-dropdown'

type ActiveTab = 'saved' | 'archived'

type FilterChip = 'Creator' | 'VTR' | 'Codebeamer'

const FILTER_CHIPS: FilterChip[] = ['Creator', 'VTR', 'Codebeamer']

type SortOption = 'date-created' | 'date-updated' | 'name-asc' | 'name-desc'

const SORT_LABELS: Record<SortOption, string> = {
  'date-created': 'Date created (most recent)',
  'date-updated': 'Date updated (most recent)',
  'name-asc': 'Sort ascending (A–Z)',
  'name-desc': 'Sort descending (Z–A)',
}

/** Reverse lookup: GraphQL enum value → internal short key (e.g. "ODD_ELEMENT_TYPE_STOP_SIGN" → "STOP_SIGN") */
const ELEMENT_TYPE_VALUE_TO_SHORT_KEY: Record<string, string> =
  Object.fromEntries(
    Object.entries(ODD_TYPE_TO_ELEMENT_TYPE).map(([key, val]) => [val, key]),
  )

const styles = {
  // Master-style 50/50 tabs, used only in the assign-trip modal (no top-bar
  // header). The main screen renders SavedTripsHeaderTabs instead.
  tab:
    (active: boolean) =>
    (theme: {
      semantic: {
        ColorAccentDefault: string
        ColorContentDefault: string
        ColorContentReduced: string
      }
    }) => ({
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      padding: '10px 0',
      cursor: 'pointer',
      borderTop: 'none',
      borderLeft: 'none',
      borderRight: 'none',
      borderBottom: active
        ? `2px solid ${theme.semantic.ColorAccentDefault}`
        : '2px solid transparent',
      color: active
        ? theme.semantic.ColorContentDefault
        : theme.semantic.ColorContentReduced,
      fontWeight: active ? 700 : 400,
      fontSize: 14,
      userSelect: 'none' as const,
      background: 'none',
    }),
  badge: (theme: {
    semantic: { ColorAccessoryReduced: string; ColorContentReduced: string }
  }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: theme.semantic.ColorAccessoryReduced,
    color: theme.semantic.ColorContentReduced,
    fontSize: 11,
    fontWeight: 600,
    padding: '0 5px',
  }),
  chip:
    (active: boolean) =>
    (theme: {
      semantic: {
        ColorAccessoryReduced: string
        ColorAccentDefault: string
        ColorContentDefault: string
      }
    }) => ({
      display: 'inline-flex',
      alignItems: 'center',
      height: 28,
      borderRadius: 14,
      padding: '0 12px',
      fontSize: 13,
      cursor: 'pointer',
      border: `1.5px dashed ${active ? theme.semantic.ColorAccentDefault : theme.semantic.ColorAccessoryReduced}`,
      color: theme.semantic.ColorContentDefault,
      background: 'transparent',
      userSelect: 'none' as const,
    }),
}

type SavedRouteDetailsForMap = NonNullable<
  RouteBuilderSavedRouteDetailsQuery['savedRouteDetails']
>

type DetailsPanelActions = {
  close: () => void
  edit: (trip: SavedTrip) => void
  duplicate: (trip: SavedTrip) => void
  rename: (trip: SavedTrip) => void
  manageVtr: (trip: SavedTrip) => void
  archive: (trip: SavedTrip) => void
  assign: (trip: SavedTrip) => void
  copyLink: (trip: SavedTrip) => void
}

const tripToWaypoints = (
  trip: SavedRouteDetailsForMap | null | undefined,
): Waypoint[] =>
  (trip?.trip?.segments ?? [])
    .map(({ segment }) => segment)
    .filter(segment => !!segment)
    .map((segment, i) => {
      const location = segment.stop?.desiredLocation ?? segment.destination
      return {
        id: segment.id,
        lat: location?.latitude ?? null,
        lng: location?.longitude ?? null,
        address: segment.stop?.desiredAddress ?? `${i + 1}`,
        // Saved route waypoints are validated if they have coordinates — the
        // segment.route field only holds outgoing leg geometry and is absent on
        // the last waypoint, which would otherwise always show as orange.
        validated: location?.latitude != null && location?.longitude != null,
        oddElementType:
          segment.metadata.find(([key]) => key === 'odd_element_type')?.[1] ??
          null,
        desiredLat: null,
        desiredLng: null,
        // heading === 0 is the backend default meaning "no heading set"; treat as null
        heading: segment.stop?.desiredLocation?.heading || null,
      }
    })

type SavedRouteDetails = NonNullable<
  RouteBuilderSavedRouteDetailsQuery['savedRouteDetails']
>

const detailsToEditingTrip = (
  savedRouteDetails: SavedRouteDetails,
  duplicateName: string,
): EditingTrip | null => {
  const savedTrip = savedRouteDetails.trip
  if (!savedTrip) return null

  const segments = savedTrip.segments.flatMap(({ segment }) => {
    const desiredLocation =
      segment?.stop?.desiredLocation ?? segment?.destination

    if (!segment || !desiredLocation) return []

    return [
      {
        id: segment.id,
        sequenceId: segment.sequenceId,
        metadata: segment.metadata,
        destination: {
          desiredLocation: {
            ...desiredLocation,
            heading: segment.stop?.desiredLocation?.heading || null,
          },
          snappedLocation: segment.stop?.snappedLocation,
          address: segment.stop?.desiredAddress,
          stopType: segment.stop?.destinationBehavior?.stopType,
          oddElementType: segment.stop?.oddElementType,
        },
        numPassengers: segment.numPassengers,
        route: segment.route,
      },
    ]
  })

  return {
    id: '',
    tripId: null,
    name: duplicateName,
    description: savedRouteDetails.description,
    tripType: savedTrip.type,
    segments,
    codebeamerId: savedRouteDetails.assignedVtrs[0]?.codebeamerIds[0],
    associatedVtr: savedRouteDetails.assignedVtrs[0]?.vtr,
    assignedVtrs: savedRouteDetails.assignedVtrs,
    allWaypointsMode: savedTrip.allWaypointsMode,
  }
}

export const SavedTripsView = ({
  metropolisId,
  mapCenter,
  assigningVehicle,
  onNavigateToCreate,
  onNavigateToEdit,
  inEditorOverride,
  onAssignSuccess,
}: SavedTripsViewProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()
  const { required } = usePaceValidators()
  const boumClaims = useBoumClaimsConsumer()
  const currentUserId = boumClaims.userId()
  const rawUsername = boumClaims.backofficeUsername()
  const groupMatch = rawUsername.match(/\+(\w+)/)
  const currentUserDisplayName = groupMatch
    ? `${groupMatch[1].charAt(0).toUpperCase()}${groupMatch[1].slice(1)} User`
    : rawUsername || 'Me'

  const apolloClient = useApolloClient()
  const {
    setMapData,
    setOverlayOptions,
    setDetailsPanel,
    setDetailsPanelOpen,
    fullBleedMap,
  } = useSharedMapContext()
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null)
  const [validatedRouteCoords, setValidatedRouteCoords] = useState<
    [number, number][]
  >([])
  const [dispatchState, setDispatchState] = useState<{
    savedTripId: string
    vin: string
    status: DispatchingStatus | null
  } | null>(null)
  const dispatchPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  // Tab selection is lifted into the header on the main screen. In the assign-trip
  // modal (no header, no provider) it falls back to local state and the in-card tabs.
  const headerTabs = useOptionalSavedTripsTabs()
  const [localActiveTab, setLocalActiveTab] = useState<ActiveTab>('saved')
  const activeTab = headerTabs?.activeTab ?? localActiveTab
  const setActiveTab = headerTabs?.setActiveTab ?? setLocalActiveTab
  const [activeChips, setActiveChips] = useState<Set<FilterChip>>(new Set())
  const [sortOption, setSortOption] = useState<SortOption>('name-asc')

  const toggleChip = (chip: FilterChip) =>
    setActiveChips(prev => {
      const next = new Set(prev)
      if (next.has(chip)) next.delete(chip)
      else next.add(chip)
      return next
    })
  const [isSortOpen, setIsSortOpen] = useState(false)
  const { getReferenceProps: getSortRefProps, ...sortMenuProps } =
    useActionMenu({
      isOpen: isSortOpen,
      setIsOpen: setIsSortOpen,
    })

  // Dialogs
  const assignVehicleDialog = useDialog()
  const renameDialog = useDialog()
  const vtrDialog = useDialog()
  const [dialogTrip, setDialogTrip] = useState<SavedTrip | null>(null)
  const [renameTrip, setRenameTrip] = useState<SavedTrip | null>(null)
  const [vtrTrip, setVtrTrip] = useState<SavedTrip | null>(null)
  const {
    control: assignControl,
    handleSubmit: handleAssignSubmit,
    reset: resetAssignForm,
  } = useForm<AssignVehicleFormValues>({
    defaultValues: { vehicle: null },
  })
  // Text typed into the vehicle-number combobox; drives client-side filtering.
  const [vehicleQuery, setVehicleQuery] = useState('')

  // Always fetch all routes; creator filtering is done client-side so multi-chip
  // OR logic can union creator routes with other chip results correctly.
  // network-only ensures a fresh fetch (and a loading spinner) every time the
  // user returns from the editor, so a newly saved route is always visible
  // without a manual page refresh.
  const { data, loading, refetch } = useErrorHandlingQuery(
    useRouteBuilderSavedRoutesQuery,
    {
      variables: {
        metropolisId,
        filter: undefined,
      },
      fetchPolicy: 'network-only',
    },
  )
  const allTrips = data?.savedRoutes ?? []

  // Shareable deep link. Only the primary saved-trips screen owns the URL — the
  // assign/create modals render their own SavedTripsView without the header-tabs
  // provider, so they must not touch the query string (hence the isPrimaryScreen
  // gate). The selected route is mirrored into `?trip=<id>` so the link can be
  // copied and reopened; opening such a link selects the route and opens its
  // details panel once the trip list for the metro has loaded.
  const isPrimaryScreen = headerTabs != null
  const [tripParam, setTripParam] = useQueryState('trip')
  const didInitFromUrlRef = useRef(false)
  useEffect(() => {
    if (!isPrimaryScreen || didInitFromUrlRef.current) return
    if (!tripParam) {
      didInitFromUrlRef.current = true
      return
    }
    const trip = allTrips.find(t => t.id === tripParam)
    if (!trip) return // wait for the trip list to load
    didInitFromUrlRef.current = true
    setSelectedTripId(trip.id)
    setDialogTrip(trip)
  }, [isPrimaryScreen, tripParam, allTrips])
  useEffect(() => {
    if (!isPrimaryScreen) return
    void setTripParam(selectedTripId ?? null)
  }, [isPrimaryScreen, selectedTripId, setTripParam])

  // Resolve creator display names for unique creator IDs that aren't "system" or empty.
  // Fetches once per unique ID and caches them in a map.
  const [fetchUserById] = useRouteBuilderBackofficeUserByIdLazyQuery()
  const [creatorNames, setCreatorNames] = useState<Record<string, string>>({})
  const fetchedIdsRef = useRef(new Set<string>())

  useEffect(() => {
    const uniqueIds = [
      ...new Set(
        allTrips
          .map(t => t.createdById)
          .filter(id => id && id !== 'system' && id !== currentUserId),
      ),
    ]
    for (const id of uniqueIds) {
      if (fetchedIdsRef.current.has(id)) continue
      fetchedIdsRef.current.add(id)
      fetchUserById({ variables: { id } }).then(result => {
        const user = result.data?.backofficeUserById
        if (user?.__typename === 'BackofficeUser') {
          setCreatorNames(prev => ({
            ...prev,
            [id]: `${user.firstName} ${user.lastName}`.trim(),
          }))
        }
      })
    }
  }, [allTrips, currentUserId, fetchUserById])

  // Collect all VTR keys in the current result set for the savedTripsByVtrs query
  const allVtrKeys = useMemo(
    () => [...new Set(allTrips.flatMap(t => t.assignedVtrs.map(v => v.vtr)))],
    [allTrips],
  )

  // savedTripsByVtrs: only fetched when VTR chip is active
  const { data: vtrData, loading: vtrLoading } = useErrorHandlingQuery(
    useRouteBuilderSavedTripsByVtrsQuery,
    {
      variables: { associatedVtrs: allVtrKeys },
      skip: !activeChips.has('VTR') || allVtrKeys.length === 0,
    },
  )

  // IDs returned by savedTripsByVtrs (deduplicated)
  const vtrTripIds = useMemo(() => {
    if (!activeChips.has('VTR') || !vtrData) return null
    return new Set(
      vtrData.savedTripsByVtrs.flatMap(entry =>
        entry.savedTrips.map(t => t.id),
      ),
    )
  }, [activeChips, vtrData])

  const { data: selectedRouteDetails, loading: detailsLoading } =
    useErrorHandlingQuery(useRouteBuilderSavedRouteDetailsQuery, {
      variables: { id: selectedTripId ?? '' },
      skip: !selectedTripId,
      fetchPolicy: 'cache-and-network',
    })

  const [validatedOddElements, setValidatedOddElements] = useState<
    ODDMapElement[]
  >([])
  const [fetchingOdds, setFetchingOdds] = useState(false)

  const [validateRouteInternal] = useErrorHandlingMutation(
    useRouteBuilderValidateRouteInternalMutation,
  )
  const [fetchTaxonomies] = useOverwatchMapListTaxonomiesLazyQuery()
  const [fetchOddElements] = useRouteBuilderOddElementsForRouteLazyQuery()

  // Mirror how TripEditorView resolves a fleet: pick the first fleet whose
  // metropolisId matches, falling back to the first fleet overall.
  const { data: overwatchFleetsData } = useErrorHandlingQuery(
    useRouteBuilderOverwatchFleetsQuery,
    { variables: {} },
  )
  const fleetId = useMemo(() => {
    const fleets = overwatchFleetsData?.overwatchFleets ?? []
    const match = fleets.find(f => f.metropolisId === metropolisId) ?? fleets[0]
    return match?.integrationId || match?.id || undefined
  }, [overwatchFleetsData, metropolisId])

  // Fleets in the current metropolis — used to scope the assignable vehicle
  // roster so a vehicle number only ever resolves to a VIN within this metro.
  const metropolisFleetIds = useMemo(
    () =>
      (overwatchFleetsData?.overwatchFleets ?? [])
        .filter(f => f.metropolisId === metropolisId)
        .map(f => f.id),
    [overwatchFleetsData, metropolisId],
  )

  // Vehicle roster for the assign dialog. `name` is the human-facing vehicle
  // number; we keep the vin alongside so the mutation can be called with it.
  const { data: overwatchVehiclesData, loading: vehiclesLoading } =
    useErrorHandlingQuery(useRouteBuilderOverwatchVehiclesQuery, {
      variables: { fleetIds: metropolisFleetIds },
      skip: metropolisFleetIds.length === 0,
    })
  const vehicleOptions = useMemo<VehicleOption[]>(
    () =>
      (overwatchVehiclesData?.overwatchVehicles ?? [])
        .map(v => ({ vin: v.vin, number: v.name }))
        .sort((a, b) => a.number.localeCompare(b.number)),
    [overwatchVehiclesData],
  )
  const filteredVehicleOptions = useMemo(
    () =>
      vehicleOptions.filter(o =>
        o.number.toLowerCase().includes(vehicleQuery.toLowerCase()),
      ),
    [vehicleOptions, vehicleQuery],
  )

  // Holds the currently-selected trip ID so async callbacks can read the
  // live value (unlike the closure-captured selectedTripId).
  const selectedTripIdRef = useRef<string | null>(selectedTripId)
  selectedTripIdRef.current = selectedTripId

  // Tracks the trip ID for which a validateRouteInternal call is currently
  // in-flight. Prevents cache-and-network double-fire from launching two
  // concurrent mutations for the same trip. Cleared on completion so that
  // a later retry (e.g. after a network error) can proceed.
  const validationInFlightForRef = useRef<string | null>(null)

  // Effect 0: clear coords and odds immediately on trip switch.
  useEffect(() => {
    setValidatedRouteCoords([])
    setValidatedOddElements([])
    setFetchingOdds(false)
    validationInFlightForRef.current = null
  }, [selectedTripId])

  // Effect 1: resolve route coordinates for the selected saved trip.
  // Newly saved routes include route geometry. Old routes have route: null and fall
  // through to re-validate via validateRouteInternal as a best-effort fallback.
  useEffect(() => {
    const details = selectedRouteDetails?.savedRouteDetails
    if (!details || details.id !== selectedTripId) {
      setValidatedRouteCoords([])
      return
    }
    if (!details.trip) return

    const tripIdAtCallTime = selectedTripId

    const hasRouteCoords = details.trip.segments.some(
      ({ segment }) => (segment?.route?.coordinates?.length ?? 0) > 0,
    )
    if (hasRouteCoords) {
      // Guard against cache-and-network double-fire setting coords twice.
      if (validationInFlightForRef.current === selectedTripId) return
      validationInFlightForRef.current = selectedTripId
      setValidatedRouteCoords(
        details.trip.segments.flatMap(({ segment }) =>
          (segment?.route?.coordinates ?? []).map(
            ({ latitude, longitude }) =>
              [longitude, latitude] as [number, number],
          ),
        ),
      )
      return
    }

    const segments = detailsToSegmentInputs(
      details,
      (details.trip.type as TripType) ?? TripType.TripTypeInternal,
    )
    if (segments.length < 2) return
    // Without fleetId the server silently returns segments: [] — wait for it.
    if (!fleetId) return
    // Skip if a validation is already in-flight for this trip. cache-and-network
    // delivers data twice; without this guard two mutations race and the later
    // one overwrites validatedRouteCoords, re-triggering Effect 2 and wiping ODDs.
    if (validationInFlightForRef.current === selectedTripId) return
    validationInFlightForRef.current = selectedTripId

    void validateRouteInternal({
      variables: { segments, fleetId, useFleetOsRoute: true },
    })
      .then(result => {
        if (selectedTripIdRef.current !== tripIdAtCallTime) return
        const validated = result?.data?.validateRouteInternal?.segments ?? []
        setValidatedRouteCoords(
          validated.flatMap(seg =>
            (seg.route?.coordinates ?? []).map(
              ({ latitude, longitude }) =>
                [longitude, latitude] as [number, number],
            ),
          ),
        )
      })
      .finally(() => {
        // Clear in-flight marker so a retry is possible if validation failed.
        if (validationInFlightForRef.current === tripIdAtCallTime) {
          validationInFlightForRef.current = null
        }
      })
  }, [fleetId, selectedRouteDetails, selectedTripId, validateRouteInternal])

  // Effect 2: fetch ODD elements whenever route coordinates change.
  // Runs independently so validation is never blocked by ODD loading.
  // selectedTripId is intentionally NOT in deps: it changes in the same commit
  // as Effect 0's clear, but validatedRouteCoords still holds the old value in
  // that render. By only reacting to validatedRouteCoords we're guaranteed the
  // coords have already been updated (either cleared by Effect 0, or freshly set
  // by Effect 1) before we kick off the ODD fetch.
  useEffect(() => {
    setValidatedOddElements([])
    setFetchingOdds(false)
    if (validatedRouteCoords.length < 2) return

    const tripIdAtCallTime = selectedTripIdRef.current
    setFetchingOdds(true)

    fetchTaxonomies({
      variables: { metroId: metropolisId },
      errorPolicy: 'ignore',
    }).then(taxResult => {
      const taxonomies = taxResult.data?.taxonomies ?? []
      if (taxonomies.length === 0) {
        setFetchingOdds(false)
        return
      }
      fetchOddElements({
        variables: {
          routes: [
            {
              routeCoordinates: validatedRouteCoords.map(([lng, lat]) => ({
                latitude: lat,
                longitude: lng,
              })),
            },
          ],
          taxonomies,
          metroId: metropolisId,
        },
        errorPolicy: 'ignore',
        fetchPolicy: 'network-only',
      })
        .then(result => {
          if (selectedTripIdRef.current !== tripIdAtCallTime) return
          const elements = (result.data?.oddElementsForRoute ?? []).flatMap(
            route =>
              route.oddElements
                .filter(e => e.location)
                .map(e => {
                  const rawType = e.elementType ?? null
                  const type =
                    (rawType && ELEMENT_TYPE_VALUE_TO_SHORT_KEY[rawType]) ??
                    getTypeFromTaxonomy(e.taxonomy) ??
                    'OTHER'
                  return {
                    id: e.taxonomy,
                    type,
                    location: e.location!,
                    name: null as string | null,
                  }
                }),
          )
          setValidatedOddElements(elements)
        })
        .finally(() => setFetchingOdds(false))
    })
  }, [fetchOddElements, fetchTaxonomies, metropolisId, validatedRouteCoords])

  // Returns true if a trip matches the active chip filters (OR logic across chips).
  const matchesChips = useCallback(
    (trip: SavedTrip) => {
      if (activeChips.size === 0) return true
      if (activeChips.has('Creator') && trip.createdById === currentUserId)
        return true
      if (activeChips.has('VTR') && vtrTripIds?.has(trip.id)) return true
      if (
        activeChips.has('Codebeamer') &&
        trip.assignedVtrs.some(v => v.codebeamerIds.length > 0)
      )
        return true
      return false
    },
    [activeChips, vtrTripIds, currentUserId],
  )

  const matchesSearch = useCallback(
    (trip: SavedTrip) => {
      if (!searchQuery) return true
      const name = trip.name.toLowerCase()
      return searchQuery
        .toLowerCase()
        .split(/\s+/)
        .every(word => name.includes(word))
    },
    [searchQuery],
  )

  // Filter and sort trips
  const filteredTrips = useMemo(() => {
    const filtered = allTrips.filter(
      trip =>
        trip.isArchived === (activeTab === 'archived') &&
        matchesChips(trip) &&
        matchesSearch(trip),
    )
    return [...filtered].sort((a, b) => {
      switch (sortOption) {
        case 'date-created':
          return b.createdMs - a.createdMs
        case 'date-updated':
          return b.updatedMs - a.updatedMs
        case 'name-asc':
          return a.name.localeCompare(b.name)
        case 'name-desc':
          return b.name.localeCompare(a.name)
        default:
          return 0
      }
    })
  }, [allTrips, activeTab, matchesChips, matchesSearch, sortOption])

  // Tab counts reflect current chip + search filters
  const savedCount = useMemo(
    () =>
      allTrips.filter(t => !t.isArchived && matchesChips(t) && matchesSearch(t))
        .length,
    [allTrips, matchesChips, matchesSearch],
  )
  const archivedCount = useMemo(
    () =>
      allTrips.filter(t => t.isArchived && matchesChips(t) && matchesSearch(t))
        .length,
    [allTrips, matchesChips, matchesSearch],
  )

  const selectedTrip = filteredTrips.find(t => t.id === selectedTripId)
  const selectedTripDetails = selectedRouteDetails?.savedRouteDetails
  const mapWaypoints: Waypoint[] = useMemo(
    () => (selectedTrip ? tripToWaypoints(selectedTripDetails ?? null) : []),
    [selectedTrip, selectedTripDetails],
  )
  const mapRouteCoordinates: [number, number][] = validatedRouteCoords

  const mapHeadings: Record<string, number> = useMemo(
    () =>
      Object.fromEntries(
        mapWaypoints
          .filter(w => w.heading != null)
          .map(w => [w.id, w.heading as number]),
      ),
    [mapWaypoints],
  )

  const mapLegData: RouteLegInfo[] = useMemo(
    () =>
      (selectedTripDetails?.trip?.segments ?? [])
        .map(({ segment }) => segment?.route)
        .filter(
          (r): r is NonNullable<typeof r> => r != null && r.distanceMeters > 0,
        )
        .map(r => ({
          distanceMeters: r.distanceMeters,
          etaSeconds: r.etaSeconds,
        })),
    [selectedTripDetails],
  )

  const handleSelect = useCallback((trip: SavedTrip) => {
    setSelectedTripId(prev => {
      if (prev === trip.id) {
        setDialogTrip(null)
        return null
      }
      setDialogTrip(trip)
      return trip.id
    })
  }, [])

  const handlePrefetch = useCallback(
    (trip: SavedTrip) => {
      apolloClient.query<RouteBuilderSavedRouteDetailsQuery>({
        query: RouteBuilderSavedRouteDetailsDocument,
        variables: { id: trip.id },
      })
    },
    [apolloClient],
  )

  // Suppress the setMapData effect after navigating to edit so that
  // SavedTripsView can't overwrite fitWaypoints:true set by TripEditorView.
  // useLocation().pathname may still reflect the old path on the render cycle
  // immediately after navigate(), so this ref provides reliable suppression.
  const navigatedToEditorRef = useRef(false)

  const handleEdit = useCallback(
    async (trip: SavedTrip) => {
      navigatedToEditorRef.current = true
      setSelectedTripId(trip.id)

      try {
        const { data } =
          await apolloClient.query<RouteBuilderSavedRouteDetailsQuery>({
            query: RouteBuilderSavedRouteDetailsDocument,
            variables: { id: trip.id },
          })
        const details = data.savedRouteDetails
        // Pass through currently-resolved coords + odds so the map stays
        // populated until TripEditorView re-validates the route.
        const editWaypoints = tripToWaypoints(details)
        setMapData({
          center: mapCenter,
          mode: 'editor',
          waypoints: editWaypoints,
          routeCoordinates: validatedRouteCoords,
          violations: [],
          sdsViolations: [],
          fitWaypoints: true,
          fitKey: `edit-${trip.id}`,
          oddElements: validatedOddElements,
          vehicleMarker: undefined,
          headings: Object.fromEntries(
            editWaypoints
              .filter(w => w.heading != null)
              .map(w => [w.id, w.heading as number]),
          ),
          pendingHeadingWaypointId: null,
          padding: computeMapPadding(true),
        })
        if (onNavigateToEdit) {
          onNavigateToEdit(trip.id)
        } else {
          navigate(
            RouteBuilderAreaRoutes.EditTrip.route.replace(':id', trip.id),
          )
        }
      } catch {
        navigatedToEditorRef.current = false
      }
    },
    [
      apolloClient,
      mapCenter,
      navigate,
      onNavigateToEdit,
      setMapData,
      validatedOddElements,
      validatedRouteCoords,
    ],
  )

  const handleDetails = useCallback((trip: SavedTrip) => {
    setSelectedTripId(trip.id)
    setDialogTrip(trip)
  }, [])

  const handleCloseDetailsPanel = useCallback(() => {
    setDialogTrip(null)
    setSelectedTripId(null)
    setDetailsPanelOpen(false)
  }, [setDetailsPanelOpen])

  const handleCopyLink = useCallback(
    (trip: SavedTrip) => {
      const url = new URL(window.location.href)
      url.searchParams.set('trip', trip.id)
      void navigator.clipboard.writeText(url.toString())
      enqueueMoiaSuccessNotification('Route link copied to clipboard')
    },
    [enqueueMoiaSuccessNotification],
  )

  // Switching tabs (from the header or the in-card tabs) clears any open route
  // selection so a trip from the previous tab doesn't stay selected on the map.
  useEffect(() => {
    handleCloseDetailsPanel()
  }, [activeTab])

  const [setSavedTripArchived, { loading: archiving }] =
    useErrorHandlingMutation(useRouteBuilderSetSavedTripArchivedMutation, {
      onCompleted: data => {
        const archived = data.setSavedTripArchived?.isArchived
        enqueueMoiaSuccessNotification(
          archived ? 'Archived route' : 'Restored route',
        )
        handleCloseDetailsPanel()
        refetch()
      },
    })

  const handleArchive = useCallback(
    (trip: SavedTrip) => {
      setSavedTripArchived({
        variables: { id: trip.id, isArchived: !trip.isArchived },
      })
    },
    [setSavedTripArchived],
  )

  const handleDuplicate = useCallback(
    async (trip: SavedTrip) => {
      const duplicateName = `${trip.name} (copy)`
      navigatedToEditorRef.current = true
      setSelectedTripId(trip.id)
      setDialogTrip(null)
      setDetailsPanel(null)
      setDetailsPanelOpen(false)

      const { data } =
        await apolloClient.query<RouteBuilderSavedRouteDetailsQuery>({
          query: RouteBuilderSavedRouteDetailsDocument,
          variables: { id: trip.id },
        })
      const duplicateFrom = data.savedRouteDetails
        ? detailsToEditingTrip(data.savedRouteDetails, duplicateName)
        : null

      if (duplicateFrom) {
        const dupWaypoints = duplicateFrom.segments.map(seg => ({
          id: seg.id,
          lat: seg.destination.desiredLocation.latitude,
          lng: seg.destination.desiredLocation.longitude,
          address:
            seg.destination.address ??
            `${seg.destination.desiredLocation.latitude.toFixed(6)}, ${seg.destination.desiredLocation.longitude.toFixed(6)}`,
          validated:
            seg.destination.desiredLocation.latitude != null &&
            seg.destination.desiredLocation.longitude != null,
          oddElementType: seg.destination.oddElementType ?? null,
          desiredLat: null,
          desiredLng: null,
          heading: seg.destination.desiredLocation.heading || null,
        }))
        setMapData({
          center: mapCenter,
          mode: 'editor',
          waypoints: dupWaypoints,
          routeCoordinates: duplicateFrom.segments.flatMap(seg =>
            (seg.route?.coordinates ?? []).map(
              ({ latitude, longitude }) =>
                [longitude, latitude] as [number, number],
            ),
          ),
          violations: [],
          sdsViolations: [],
          fitWaypoints: true,
          fitKey: `duplicate-${trip.id}`,
          oddElements: [],
          vehicleMarker: undefined,
          headings: Object.fromEntries(
            dupWaypoints
              .filter(w => w.heading != null)
              .map(w => [w.id, w.heading as number]),
          ),
          pendingHeadingWaypointId: null,
          padding: computeMapPadding(true),
        })
        if (onNavigateToCreate) {
          onNavigateToCreate({
            duplicateFrom,
            duplicateFromTripId: trip.id,
          })
        } else {
          navigate(RouteBuilderAreaRoutes.CreateTrip.route, {
            state: { duplicateFrom, duplicateFromTripId: trip.id },
          })
        }
      } else {
        navigatedToEditorRef.current = false
      }
    },
    [
      apolloClient,
      mapCenter,
      navigate,
      onNavigateToCreate,
      setDetailsPanel,
      setDetailsPanelOpen,
      setMapData,
    ],
  )

  const handleRename = useCallback(
    (trip: SavedTrip) => {
      setRenameTrip(trip)
      renameDialog.openDialog()
    },
    [renameDialog],
  )

  const handleManageVtr = useCallback(
    (trip: SavedTrip) => {
      setVtrTrip(trip)
      setSelectedTripId(trip.id)
      vtrDialog.openDialog()
    },
    [vtrDialog],
  )

  const [assignSavedTripMutation, { loading: assigning }] =
    useErrorHandlingMutation(useRouteBuilderAssignSavedTripMutation)

  const [fetchDispatchingStatus] = useRouteBuilderDispatchingStatusLazyQuery()

  const dispatchClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const stopDispatchPolling = useCallback(() => {
    if (dispatchPollRef.current) {
      clearInterval(dispatchPollRef.current)
      dispatchPollRef.current = null
    }
    if (dispatchClearTimeoutRef.current) {
      clearTimeout(dispatchClearTimeoutRef.current)
      dispatchClearTimeoutRef.current = null
    }
  }, [])

  // Clear the interval and any pending clear-timeout when the component unmounts.
  useEffect(() => () => stopDispatchPolling(), [stopDispatchPolling])

  const startDispatchPolling = useCallback(
    (savedTripId: string, vin: string) => {
      stopDispatchPolling()
      const poll = async () => {
        const result = await fetchDispatchingStatus({
          variables: { savedTripId, vin },
          fetchPolicy: 'network-only',
        })
        const status = (
          result.data as RouteBuilderDispatchingStatusQuery | undefined
        )?.dispatchingStatus?.dispatchingStatus
        if (!status) return
        setDispatchState({ savedTripId, vin, status })
        const terminal =
          status === DispatchingStatus.DispatchingStatusRouteDispatchSuccess ||
          status === DispatchingStatus.DispatchingStatusRouteDispatchFailed ||
          status === DispatchingStatus.DispatchingStatusRouteValidationFailed
        if (terminal) {
          stopDispatchPolling()
          if (
            status === DispatchingStatus.DispatchingStatusRouteDispatchSuccess
          ) {
            enqueueMoiaSuccessNotification(
              `Trip dispatched successfully to ${vin}`,
            )
            onAssignSuccess?.()
          }
          dispatchClearTimeoutRef.current = setTimeout(
            () => setDispatchState(null),
            4000,
          )
        }
      }
      void poll()
      dispatchPollRef.current = setInterval(() => void poll(), 2000)
    },
    [
      fetchDispatchingStatus,
      stopDispatchPolling,
      enqueueMoiaSuccessNotification,
      onAssignSuccess,
    ],
  )

  const handleOpenAssign = useCallback(
    (trip: SavedTrip) => {
      if (assigningVehicle) {
        // VIN already known — skip the dialog and assign directly
        assignSavedTripMutation({
          variables: { savedTripId: trip.id, vin: assigningVehicle.vin },
          onCompleted: () => {
            startDispatchPolling(trip.id, assigningVehicle.vin)
          },
        })
        return
      }
      setDialogTrip(trip)
      resetAssignForm({ vehicle: null })
      setVehicleQuery('')
      assignVehicleDialog.openDialog()
    },
    [
      assigningVehicle,
      assignVehicleDialog,
      assignSavedTripMutation,
      startDispatchPolling,
      resetAssignForm,
    ],
  )

  const handleAssignVehicle = useCallback(
    ({ vehicle }: AssignVehicleFormValues) => {
      if (!vehicle || !dialogTrip) return

      assignVehicleDialog.closeDialog()

      assignSavedTripMutation({
        variables: { savedTripId: dialogTrip.id, vin: vehicle.vin },
        onCompleted: () => {
          startDispatchPolling(dialogTrip.id, vehicle.vin)
        },
      })
    },
    [
      assignVehicleDialog,
      dialogTrip,
      assignSavedTripMutation,
      startDispatchPolling,
    ],
  )

  const detailsPanelActionsRef = useRef<DetailsPanelActions>(
    null as unknown as DetailsPanelActions,
  )

  detailsPanelActionsRef.current = {
    close: handleCloseDetailsPanel,
    edit: handleEdit,
    duplicate: handleDuplicate,
    rename: handleRename,
    manageVtr: handleManageVtr,
    archive: handleArchive,
    assign: handleOpenAssign,
    copyLink: handleCopyLink,
  }

  // Update panel content whenever trip or waypoints change (never re-triggers animation)
  useEffect(() => {
    if (!dialogTrip) return
    const isDispatching =
      assigning ||
      (dispatchState != null &&
        dispatchState.status !==
          DispatchingStatus.DispatchingStatusRouteDispatchSuccess &&
        dispatchState.status !==
          DispatchingStatus.DispatchingStatusRouteDispatchFailed &&
        dispatchState.status !==
          DispatchingStatus.DispatchingStatusRouteValidationFailed)
    setDetailsPanel(
      <SavedRouteDetailsPanel
        trip={dialogTrip}
        waypoints={mapWaypoints}
        legData={mapLegData}
        oddElements={validatedOddElements}
        fetchingOdds={fetchingOdds}
        loading={detailsLoading}
        archiving={archiving}
        assigning={isDispatching}
        onClose={() => detailsPanelActionsRef.current.close()}
        onEdit={trip => {
          detailsPanelActionsRef.current.close()
          detailsPanelActionsRef.current.edit(trip)
        }}
        onDuplicate={trip => {
          detailsPanelActionsRef.current.close()
          detailsPanelActionsRef.current.duplicate(trip)
        }}
        onRename={trip => {
          detailsPanelActionsRef.current.close()
          detailsPanelActionsRef.current.rename(trip)
        }}
        onManageVtr={trip => {
          detailsPanelActionsRef.current.close()
          detailsPanelActionsRef.current.manageVtr(trip)
        }}
        onArchive={trip => {
          detailsPanelActionsRef.current.archive(trip)
        }}
        onAssign={trip => {
          detailsPanelActionsRef.current.assign(trip)
        }}
        onCopyLink={
          isPrimaryScreen
            ? trip => detailsPanelActionsRef.current.copyLink(trip)
            : undefined
        }
        dispatchStatus={dispatchState?.status ?? null}
        hideActions={!headerTabs}
        allWaypointsMode={selectedTripDetails?.trip?.allWaypointsMode ?? false}
      />,
    )
  }, [
    dialogTrip,
    mapWaypoints,
    mapLegData,
    selectedTripDetails,
    validatedOddElements,
    fetchingOdds,
    archiving,
    assigning,
    dispatchState,
    detailsLoading,
    setDetailsPanel,
    headerTabs,
    isPrimaryScreen,
  ])

  // Open/close animation driven only by whether dialogTrip is set
  useEffect(() => {
    setDetailsPanelOpen(!!dialogTrip)
  }, [dialogTrip, setDetailsPanelOpen])

  // Register overlay options for the map top-right panel.
  // Skip when in editor — TripEditorView owns overlayOptions while it's mounted.
  // Updating here while the editor is active would overwrite TripEditorView's options
  // (e.g. resetting autoEnableLanes back to false), causing lag and incorrect state.
  // TripEditorView also has no cleanup on unmount so when it leaves, the next render
  // of this effect fires and re-registers the saved-trips options cleanly.
  useEffect(() => {
    const inEditor =
      inEditorOverride ??
      location.pathname
        .split('/')
        .filter(Boolean)
        .some(s => ['create', 'create-from-vtr', 'edit', 'assign'].includes(s))
    if (inEditor) return
    setOverlayOptions({
      metropolisId,
      autoEnableLanes: false,
      autoEnableGeonets: true,
      routeOddElements: validatedOddElements,
    })
  }, [
    setOverlayOptions,
    metropolisId,
    validatedOddElements,
    location.pathname,
    inEditorOverride,
  ])

  // Push map data props into the shared persistent RouteMap.
  // When a trip is selected the details panel is open (detailsPanelOpen=true) and
  // overlay options are always present here, so we compute the correct padding
  // eagerly so the fit effect uses the right inset from the very first render.
  useEffect(() => {
    // When navigating to an editor path (create/edit/assign), skip pushing mode:
    // 'overview' — TripEditorView will mount and set mode:'editor' + fitWaypoints.
    // Pushing 'overview' here starts a competing flyTo that cancels the fit animation.
    const inEditor =
      inEditorOverride ??
      location.pathname
        .split('/')
        .filter(Boolean)
        .some(s => ['create', 'create-from-vtr', 'edit', 'assign'].includes(s))
    if (inEditor) {
      // URL has caught up to the editor path (or modal override) — the ref is no
      // longer needed. Reset it so that returning to saved-trips will work.
      navigatedToEditorRef.current = false
      return
    }

    // Pathname says saved-trips but navigate() may have just been called for
    // an editor path. The ref guards the one-render window before the URL updates.
    if (navigatedToEditorRef.current) {
      return
    }

    // In the vehicle-monitoring assign-vehicle flow keep the flat editor camera
    // (no overview 45° tilt/flyTo) and keep the vehicle marker so it stays on the
    // map. Fitting to a selected trip still happens — it uses pitch/bearing 0, so
    // it zooms/moves to the route without tilting.
    setMapData({
      center: mapCenter,
      mode: assigningVehicle || selectedTrip ? 'editor' : 'overview',
      waypoints: mapWaypoints,
      routeCoordinates: mapRouteCoordinates,
      violations: [],
      sdsViolations: [],
      fitWaypoints: !!selectedTrip,
      fitKey: selectedTripId,
      oddElements: validatedOddElements,
      vehicleMarker: assigningVehicle
        ? {
            location: assigningVehicle.location,
            name: assigningVehicle.name,
            isPhantom: assigningVehicle.isPhantom,
            heading: assigningVehicle.heading ?? null,
          }
        : undefined,
      headings: mapHeadings,
      pendingHeadingWaypointId: null,
      // In the full-bleed modal the details panel floats over the map when a
      // route is selected, so reserve left clearance for it too.
      padding: computeMapPadding(
        false,
        fullBleedMap,
        fullBleedMap && !!selectedTrip,
      ),
    })
  }, [
    setMapData,
    mapCenter,
    selectedTrip,
    selectedTripId,
    mapWaypoints,
    mapHeadings,
    mapRouteCoordinates,
    validatedOddElements,
    location.pathname,
    inEditorOverride,
    assigningVehicle,
    fullBleedMap,
  ])

  return (
    <>
      {/* Search + Sort row */}
      <div
        css={theme => ({
          display: 'flex',
          gap: theme.space.Space2,
          padding: theme.space.Space3,
        })}
      >
        <Input
          size="compact"
          placeholder="Search routes"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          leading={<SearchIcon />}
          css={{ flex: 1 }}
        />
        <div css={{ position: 'relative' }}>
          <Button
            label="Sort"
            size="compact"
            variant="secondary"
            hasDropdown
            {...getSortRefProps()}
            ref={sortMenuProps.refs.setReference}
          />
          <ActionMenu {...sortMenuProps}>
            {(Object.keys(SORT_LABELS) as SortOption[]).map(opt => (
              <ActionItem
                key={opt}
                label={SORT_LABELS[opt]}
                leading={sortOption === opt ? <CheckmarkIcon /> : undefined}
                onClick={() => {
                  setSortOption(opt)
                  setIsSortOpen(false)
                }}
              />
            ))}
          </ActionMenu>
        </div>
      </div>

      {/* Filter chips — hidden for now until the backend is in place.
          Keep the filtering logic (activeChips/toggleChip/matchesChips) intact
          so the chips can be re-enabled by restoring this block. */}
      {false && (
        <div
          css={theme => ({
            display: 'flex',
            gap: theme.space.Space2,
            padding: `0 ${theme.space.Space3}`,
            paddingBottom: theme.space.Space2,
            flexWrap: 'wrap',
          })}
        >
          {FILTER_CHIPS.map(chip => (
            <button
              key={chip}
              type="button"
              css={styles.chip(activeChips.has(chip))}
              onClick={() => toggleChip(chip)}
            >
              {chip}
            </button>
          ))}
        </div>
      )}

      {/* Divider */}
      <div
        css={theme => ({
          borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
        })}
      />

      {/* Saved / Archived tabs — on the main screen these live in the view's
          top-bar header (see SavedTripsHeaderTabs), matching the hulk-trips tabs.
          The assign-trip modal has no header, so it keeps the tabs in-card here. */}
      {!headerTabs && (
        <div
          css={theme => ({
            display: 'flex',
            borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
            marginBottom: 0,
          })}
        >
          <button
            type="button"
            css={styles.tab(activeTab === 'saved')}
            onClick={() => setActiveTab('saved')}
          >
            <span>Saved</span>
            <span css={styles.badge}>{savedCount}</span>
          </button>
          <button
            type="button"
            css={styles.tab(activeTab === 'archived')}
            onClick={() => setActiveTab('archived')}
          >
            <span>Archived</span>
            <span css={styles.badge}>{archivedCount}</span>
          </button>
        </div>
      )}

      {/* Trip list — a scrolling stack of rounded route cards on a grey surface
          so the white cards stand out. */}
      <div
        css={theme => ({
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space.Space2,
          padding: theme.space.Space3,
          // Page-background token: a near-white off-white (~#FAFAF5) that stays
          // subtly offset from the white cards and flips correctly in dark mode.
          backgroundColor: theme.semantic.ColorBackgroundDefault,
        })}
      >
        {(loading || vtrLoading) && (
          <div css={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <LoadingIndicator size={24} />
          </div>
        )}
        {!loading && !vtrLoading && filteredTrips.length === 0 && (
          <div
            css={theme => ({
              textAlign: 'center',
              padding: theme.space.Space6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: theme.space.Space1,
            })}
          >
            <Text size="sm" color="reduced">
              {searchQuery ? 'No matching routes' : 'No routes yet'}
            </Text>
          </div>
        )}
        {filteredTrips.map(trip => (
          <SavedTripCard
            key={trip.id}
            trip={trip}
            selected={trip.id === selectedTripId}
            currentUserId={currentUserId}
            currentUserDisplayName={currentUserDisplayName}
            creatorNames={creatorNames}
            onSelect={handleSelect}
            onMouseEnter={handlePrefetch}
            onEdit={handleEdit}
            onDetails={handleDetails}
            onArchive={handleArchive}
            onDuplicate={handleDuplicate}
            onRename={handleRename}
            onManageVtr={handleManageVtr}
            actionsDisabled={archiving}
            hideActions={!headerTabs}
          />
        ))}
      </div>

      {/* Footer: result count on the main screen; the Build Route action lives in
          the top-bar header. In the assign-trip modal (no header) keep the
          primary Build Route button here instead. */}
      <div
        css={theme => ({
          display: 'flex',
          alignItems: 'center',
          padding: theme.space.Space3,
          borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
        })}
      >
        {headerTabs ? (
          <Text size="sm" color="reduced">
            {`${filteredTrips.length} ${filteredTrips.length === 1 ? 'result' : 'results'}`}
          </Text>
        ) : (
          <Button
            label="Build Route"
            variant="primary"
            icon={<PlusIcon />}
            onClick={() => {
              handleCloseDetailsPanel()
              if (onNavigateToCreate) {
                onNavigateToCreate()
              } else {
                navigate(RouteBuilderAreaRoutes.CreateTrip.route)
              }
            }}
            css={{ width: '100%' }}
          />
        )}
      </div>

      {assignVehicleDialog.isOpen &&
        createPortal(
          <div
            css={{
              position: 'fixed',
              inset: 0,
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
            onClick={e => {
              if (e.target === e.currentTarget)
                assignVehicleDialog.closeDialog()
            }}
          >
            {/* Zero-size host for the vehicle-number combobox menu; keeps it in
                this backdrop's stacking context (see portalId below) without
                intercepting backdrop clicks or affecting the centered layout. */}
            <div
              id={ASSIGN_VEHICLE_DROPDOWN_PORTAL_ID}
              css={{ position: 'absolute', top: 0, left: 0 }}
            />
            <div
              css={theme => ({
                width: 420,
                // White surface (matches the Create Hulk Trip form card) so the
                // combobox input reads clean white; ColorBackgroundDefault is
                // the off-white page background, not white.
                backgroundColor: theme.semantic.ColorSurfaceDefault,
                borderRadius: theme.radius.RadiusLarge,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
              })}
            >
              <div
                css={theme => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: `${theme.space.Space3} ${theme.space.Space4}`,
                  borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
                  flexShrink: 0,
                })}
              >
                <Heading size="sm">Assign route to vehicle</Heading>
                <IconButton
                  aria-label="Close"
                  size="compact"
                  variant="plain"
                  icon={<XIcon />}
                  onClick={assignVehicleDialog.closeDialog}
                />
              </div>
              <form
                id="route-builder-assign-vehicle-form"
                onSubmit={handleAssignSubmit(handleAssignVehicle)}
                css={theme => ({
                  padding: theme.space.Space4,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.space.Space3,
                })}
              >
                <FormComboboxField<
                  AssignVehicleFormValues,
                  VehicleOption | null
                >
                  control={assignControl}
                  name="vehicle"
                  label="Vehicle number"
                  placeholder={
                    vehiclesLoading
                      ? 'Loading vehicles…'
                      : 'Search by vehicle number'
                  }
                  emptyState="No vehicles found"
                  rules={{ required }}
                  // Match the 40px input height of the ODD filter modal's
                  // MultiSelect (default size), not the shorter compact size.
                  size="default"
                  options={filteredVehicleOptions}
                  query={vehicleQuery}
                  onQueryChange={setVehicleQuery}
                  onChange={option => setVehicleQuery(option?.number ?? '')}
                  optionToId={option => option?.vin ?? ''}
                  optionToQuery={option => option?.number ?? ''}
                  renderOption={option => option?.number ?? ''}
                  maxHeight="230px"
                  // The combobox menu is hard-capped at z-index 9999, which sits
                  // below this modal's backdrop (10001). Portal it into the host
                  // below so it renders inside the backdrop's stacking context —
                  // above the modal card without touching global z-indexes.
                  portalId={ASSIGN_VEHICLE_DROPDOWN_PORTAL_ID}
                />
              </form>
              <div
                css={theme => ({
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: theme.space.Space2,
                  padding: `${theme.space.Space3} ${theme.space.Space4}`,
                  borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
                })}
              >
                <Button
                  label="Cancel"
                  variant="secondary"
                  onClick={assignVehicleDialog.closeDialog}
                />
                <Button
                  label="Continue"
                  variant="primary"
                  type="submit"
                  form="route-builder-assign-vehicle-form"
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
      {renameDialog.isOpen && renameTrip && (
        <RenameDialog
          id={renameDialog.id}
          trip={renameTrip}
          onClose={renameDialog.closeDialog}
          onSuccess={() => {
            renameDialog.closeDialog()
            refetch()
          }}
        />
      )}

      {vtrDialog.isOpen && vtrTrip && (
        <VtrAssociationDialog
          id={vtrDialog.id}
          trip={vtrTrip}
          segments={detailsToSegmentInputs(
            selectedTripDetails,
            vtrTrip.tripType,
          )}
          allWaypointsMode={
            selectedTripDetails?.trip?.allWaypointsMode ?? false
          }
          onClose={vtrDialog.closeDialog}
          onSuccess={() => {
            vtrDialog.closeDialog()
            refetch()
          }}
        />
      )}
    </>
  )
}
